// This file will contain shared utility functions

function parseDate(dateString: string): { month: string; day: string; year: string } | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date format: ${dateString}. Skipping.`);
    return null;
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate().toString();
  const year = date.getFullYear().toString();

  return { month, day, year };
}

export function formatDateArray(dateStrings: string[]): string {
  if (!dateStrings || dateStrings.length === 0) {
    return '';
  }

  const parsedDates = dateStrings.map(parseDate).filter(d => d !== null) as { month: string, day: string, year: string }[];

  if (parsedDates.length === 0) {
    return '';
  }

  const firstDate = parsedDates[0];
  const allSameMonthAndYear = parsedDates.every(d =>
    d.month === firstDate.month && d.year === firstDate.year
  );

  if (allSameMonthAndYear) {
    const days = parsedDates.map(d => d.day).join(', ');
    return `${firstDate.month} ${days}`;
  } else {
    const formattedParts: string[] = [];
    const groupedByMonthYear: { [key: string]: string[] } = {};

    parsedDates.forEach(d => {
      const key = `${d.month} ${d.year}`;
      if (!groupedByMonthYear[key]) {
        groupedByMonthYear[key] = [];
      }
      groupedByMonthYear[key].push(d.day);
    });

    for (const key in groupedByMonthYear) {
      const [month, year] = key.split(' ');
      const days = groupedByMonthYear[key].join(', ');
      formattedParts.push(`${month} ${days}`);
    }
    return formattedParts.join('; ');
  }
}

export function formatDateArrayWithYear(dateStrings: string[]): string {
  if (!dateStrings || dateStrings.length === 0) {
    return '';
  }

  const parsedDates = dateStrings.map(parseDate).filter(d => d !== null) as { month: string, day: string, year: string }[];

  if (parsedDates.length === 0) {
    return '';
  }

  const firstDate = parsedDates[0];
  const allSameMonthAndYear = parsedDates.every(d =>
    d.month === firstDate.month && d.year === firstDate.year
  );

  if (allSameMonthAndYear) {
    const days = parsedDates.map(d => d.day).join(', ');
    return `${firstDate.month} ${days}, ${firstDate.year}`;
  } else {
    const formattedParts: string[] = [];
    const groupedByMonthYear: { [key: string]: string[] } = {};

    parsedDates.forEach(d => {
      const key = `${d.month} ${d.year}`;
      if (!groupedByMonthYear[key]) {
        groupedByMonthYear[key] = [];
      }
      groupedByMonthYear[key].push(d.day);
    });

    for (const key in groupedByMonthYear) {
      const [month, year] = key.split(' ');
      const days = groupedByMonthYear[key].join(', ');
      formattedParts.push(`${month} ${days}, ${year}`);
    }
    return formattedParts.join('; ');
  }
}

export function extractTime(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date format: ${dateString}`);
    return '';
  }

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12; // handle midnight (0 -> 12)

  const formattedHours = hours.toString().padStart(2, '0');

  return `${formattedHours}:${minutes} ${ampm}`;
}

